/**
 * Clarity Call availability rules — weekly hours, call length, buffer, notice, window and
 * blocked dates. Saved to /api/clarity-call-availability; Outlook busy time still applies on
 * top of whatever is set here.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { adminApi } from "services/api";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];
const TIMES = Array.from(
  { length: 96 },
  (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`
);
const END_TIMES = [...TIMES.slice(1), "24:00"];
const LENGTHS = [15, 30, 45, 60, 75, 90, 105, 120];
const BUFFERS = [0, 15, 30, 45, 60];
const NOTICE = [
  { value: 0, label: "None" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 240, label: "4 hours" },
  { value: 720, label: "12 hours" },
  { value: 1440, label: "1 day" },
  { value: 2880, label: "2 days" },
  { value: 10080, label: "7 days" },
];
const ZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["Africa/Lagos", "Europe/London", "America/New_York"];
  }
})();
const DEFAULT_INTERVAL = { start: "10:00", end: "17:00" };

const formatSlot = (iso, timeZone) =>
  new Date(iso).toLocaleString("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });

function IntervalRow({ interval, onChange, onRemove }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
      <TextField
        select
        size="small"
        value={interval.start}
        onChange={(e) => onChange({ ...interval, start: e.target.value })}
        sx={{ width: 110 }}
      >
        {TIMES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <MDTypography variant="button">–</MDTypography>
      <TextField
        select
        size="small"
        value={interval.end}
        onChange={(e) => onChange({ ...interval, end: e.target.value })}
        sx={{ width: 110 }}
      >
        {END_TIMES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <IconButton size="small" onClick={onRemove} aria-label="Remove hours">
        <Icon fontSize="small">close</Icon>
      </IconButton>
    </MDBox>
  );
}

IntervalRow.propTypes = {
  interval: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ClarityCallAvailability() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState({ loading: false, slots: [], error: "" });
  const previewTimer = useRef(null);

  useEffect(() => {
    adminApi
      .getClarityCallAvailability()
      .then((res) => setRules(res.rules))
      .catch(() => setErrors(["Couldn't load the current availability."]));
  }, []);

  // Debounced live preview of the draft rules.
  useEffect(() => {
    if (!rules) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreview((p) => ({ ...p, loading: true, error: "" }));
      try {
        const res = await adminApi.previewClarityCallAvailability(rules);
        setPreview({ loading: false, slots: res.preview || [], error: "" });
      } catch (err) {
        setPreview({
          loading: false,
          slots: [],
          error: err.response?.data?.message || "Preview unavailable.",
        });
      }
    }, 600);
    return () => clearTimeout(previewTimer.current);
  }, [rules]);

  // Any edit clears the last save's messages: a stale error box would outlive the fix.
  const update = (patch) => {
    setRules((r) => ({ ...r, ...patch }));
    setSuccess("");
    setErrors([]);
  };
  const setDay = (key, intervals) =>
    update({ weeklyHours: { ...rules.weeklyHours, [key]: intervals } });
  const setOverride = (index, patch) =>
    update({
      dateOverrides: rules.dateOverrides.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    });

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const res = await adminApi.saveClarityCallAvailability(rules);
      setRules(res.rules);
      setSuccess("Availability saved. New bookings use these rules straight away.");
    } catch (err) {
      setErrors(err.response?.data?.errors || [err.response?.data?.message || "Save failed."]);
    } finally {
      setSaving(false);
    }
  };

  if (!rules) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          {errors.length ? (
            <MDAlert color="error">{errors[0]}</MDAlert>
          ) : (
            <MDTypography variant="body2">Loading…</MDTypography>
          )}
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={3}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDBox>
              <MDTypography variant="h5" color="white">
                Clarity Call Availability
              </MDTypography>
              <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                When visitors can book. Busy time in the organizer&apos;s Outlook calendar is always
                blocked too.
              </MDTypography>
            </MDBox>
            <MDButton
              component={Link}
              to="/clarity-call-bookings"
              variant="outlined"
              color="white"
              size="small"
              startIcon={<Icon>arrow_back</Icon>}
            >
              Bookings
            </MDButton>
          </MDBox>

          <MDBox p={3}>
            {errors.length > 0 && (
              <MDBox mb={2}>
                <MDAlert color="error" dismissible onClose={() => setErrors([])}>
                  {errors.join(" ")}
                </MDAlert>
              </MDBox>
            )}
            {success && (
              <MDBox mb={2}>
                <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                  {success}
                </MDAlert>
              </MDBox>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <MDTypography variant="h6" mb={1}>
                  Weekly hours
                </MDTypography>
                {DAYS.map(({ key, label }) => {
                  const intervals = rules.weeklyHours[key] || [];
                  const on = intervals.length > 0;
                  return (
                    <MDBox
                      key={key}
                      display="flex"
                      alignItems="flex-start"
                      gap={2}
                      py={1}
                      sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                    >
                      <MDBox width={150} display="flex" alignItems="center">
                        <Switch
                          checked={on}
                          onChange={(e) =>
                            setDay(key, e.target.checked ? [{ ...DEFAULT_INTERVAL }] : [])
                          }
                        />
                        <MDTypography variant="button" fontWeight="medium">
                          {label}
                        </MDTypography>
                      </MDBox>
                      <MDBox flex={1}>
                        {!on && (
                          <MDTypography variant="caption" color="text">
                            Unavailable
                          </MDTypography>
                        )}
                        {intervals.map((interval, i) => (
                          <IntervalRow
                            key={`${key}-${i}`}
                            interval={interval}
                            onChange={(next) =>
                              setDay(
                                key,
                                intervals.map((x, j) => (j === i ? next : x))
                              )
                            }
                            onRemove={() =>
                              setDay(
                                key,
                                intervals.filter((_, j) => j !== i)
                              )
                            }
                          />
                        ))}
                        {on && (
                          <MDButton
                            size="small"
                            variant="text"
                            color="info"
                            startIcon={<Icon>add</Icon>}
                            onClick={() =>
                              setDay(key, [...intervals, { start: "14:00", end: "17:00" }])
                            }
                          >
                            Add hours
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                  );
                })}

                <MDTypography variant="h6" mt={3} mb={1}>
                  Blocked or custom dates
                </MDTypography>
                {rules.dateOverrides.map((o, index) => (
                  <MDBox
                    key={`override-${index}`}
                    display="flex"
                    flexWrap="wrap"
                    alignItems="flex-start"
                    gap={1.5}
                    mb={1.5}
                  >
                    <TextField
                      type="date"
                      size="small"
                      label="Date"
                      InputLabelProps={{ shrink: true }}
                      value={o.date}
                      onChange={(e) => setOverride(index, { date: e.target.value })}
                      sx={{ width: 170 }}
                    />
                    <MDBox display="flex" alignItems="center">
                      <Switch
                        checked={o.intervals.length > 0}
                        onChange={(e) =>
                          setOverride(index, {
                            intervals: e.target.checked ? [{ ...DEFAULT_INTERVAL }] : [],
                          })
                        }
                      />
                      <MDTypography variant="caption">
                        {o.intervals.length ? "Custom hours" : "Closed all day"}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      {o.intervals.map((interval, i) => (
                        <IntervalRow
                          key={`o-${index}-${i}`}
                          interval={interval}
                          onChange={(next) =>
                            setOverride(index, {
                              intervals: o.intervals.map((x, j) => (j === i ? next : x)),
                            })
                          }
                          onRemove={() =>
                            setOverride(index, { intervals: o.intervals.filter((_, j) => j !== i) })
                          }
                        />
                      ))}
                    </MDBox>
                    <TextField
                      size="small"
                      label="Note"
                      value={o.note || ""}
                      inputProps={{ maxLength: 100 }}
                      onChange={(e) => setOverride(index, { note: e.target.value })}
                      sx={{ width: 180 }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Remove date"
                      onClick={() =>
                        update({ dateOverrides: rules.dateOverrides.filter((_, i) => i !== index) })
                      }
                    >
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </MDBox>
                ))}
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={<Icon>event_busy</Icon>}
                  onClick={() =>
                    update({
                      dateOverrides: [
                        ...rules.dateOverrides,
                        { date: "", intervals: [], note: "" },
                      ],
                    })
                  }
                >
                  Add a date
                </MDButton>
              </Grid>

              <Grid item xs={12} md={5}>
                <MDTypography variant="h6" mb={1}>
                  Booking rules
                </MDTypography>
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <Autocomplete
                    disableClearable
                    options={ZONES}
                    value={rules.timeZone}
                    onChange={(_, value) => update({ timeZone: value })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Your time zone (hours above are in this zone)"
                      />
                    )}
                  />
                  <TextField
                    select
                    size="small"
                    label="Call length"
                    value={rules.slotMinutes}
                    onChange={(e) => update({ slotMinutes: Number(e.target.value) })}
                  >
                    {LENGTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m} minutes
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Buffer between calls"
                    value={rules.bufferMinutes}
                    onChange={(e) => update({ bufferMinutes: Number(e.target.value) })}
                  >
                    {BUFFERS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m ? `${m} minutes` : "None"}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Minimum notice"
                    value={rules.minNoticeMinutes}
                    onChange={(e) => update({ minNoticeMinutes: Number(e.target.value) })}
                    helperText="Visitors can't book, move or cancel closer to the call than this."
                  >
                    {NOTICE.map((n) => (
                      <MenuItem key={n.value} value={n.value}>
                        {n.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    size="small"
                    label="Days ahead visitors can book"
                    value={rules.windowDays}
                    inputProps={{ min: 1, max: 60 }}
                    onChange={(e) => update({ windowDays: Number(e.target.value) })}
                  />
                </MDBox>

                <Divider sx={{ my: 3 }} />
                <MDTypography variant="h6" mb={1}>
                  Next open times
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  A live preview of these (unsaved) rules against the Outlook calendar.
                </MDTypography>
                {preview.loading && <MDTypography variant="caption">Checking…</MDTypography>}
                {preview.error && (
                  <MDTypography variant="caption" color="error">
                    {preview.error}
                  </MDTypography>
                )}
                {!preview.loading && !preview.error && preview.slots.length === 0 && (
                  <MDTypography variant="caption" color="warning">
                    No open times with these rules.
                  </MDTypography>
                )}
                {preview.slots.map((s) => (
                  <MDTypography key={s.start} variant="button" display="block">
                    {formatSlot(s.start, rules.timeZone)}
                  </MDTypography>
                ))}
              </Grid>
            </Grid>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton color="info" onClick={save} disabled={saving} startIcon={<Icon>save</Icon>}>
                {saving ? "Saving…" : "Save availability"}
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ClarityCallAvailability;
